import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle } from "@untitledui/icons";
import z from "zod";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { HeaderNavigation } from "@/components/Molecules/HeaderNav/HeaderNav";
import { useAppTranslation } from "@/hooks/use-app-translation";

// Search params schema for organization name
const searchSchema = z.object({
	orgName: z.string().optional().catch("OpSource"),
	status: z.enum(["expired", "success", "error", "invalid"]).catch("error"),
	external: z.boolean().optional().catch(true),
});

export const Route = createFileRoute("/invitation")({
	validateSearch: searchSchema,
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const { status, orgName } = Route.useSearch();
	const navigate = useNavigate();

	const organizationName = orgName || "OpSource";

	const renderContent = () => {
		switch (status) {
			case "success":
				return (
					<>
						{/* Success Icon */}
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={CheckCircle}
								color="success"
								theme="modern"
								size="xl"
								className="box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.18) inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05);"
							/>
						</div>
						{/* Heading */}
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("invitation.success.title")}
						</h1>
						{/* Description */}
						<p className="mb-8 text-md text-fg-secondary">
							{t("invitation.success.description", { organizationName })}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => navigate({ to: "/login" })}
						>
							{t("login.signIn")}
						</Button>
					</>
				);

			case "expired":
				return (
					<>
						{/* Expired Icon */}
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="warning"
								theme="modern"
								size="xl"
								className="box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.18) inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05);"
							/>
						</div>
						{/* Heading */}
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("invitation.expired.title")}
						</h1>
						{/* Description */}
						<p className="mb-8 text-md text-fg-secondary">
							{t("invitation.expired.description", { organizationName })}
						</p>
					</>
				);

			case "invalid":
				return (
					<>
						{/* Invalid Icon */}
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
								className="box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.18) inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05);"
							/>
						</div>
						{/* Heading */}
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("invitation.invalid.title")}
						</h1>
						{/* Description */}
						<p className="mb-8 text-md text-fg-secondary">
							{t("invitation.invalid.description", { organizationName })}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => navigate({ to: "/login" })}
						>
							{t("login.signIn")}
						</Button>
					</>
				);
			default:
				return (
					<>
						{/* Error Icon */}
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
								className="box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.18) inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05);"
							/>
						</div>
						{/* Heading */}
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("invitation.error.title")}
						</h1>
						{/* Description */}
						<p className="mb-8 text-md text-fg-secondary">
							{t("invitation.error.description", { organizationName })}
						</p>
					</>
				);
		}
	};

	return (
		<div className="flex flex-col min-h-screen overflow-hidden">
			<HeaderNavigation
				onUserProfile={() => {}}
				items={[]}
				name={""}
				email={""}
				onSignOut={() => {}}
				homeUrl={""}
				showAvatarDropdown={false}
			/>
			<div className="flex items-center justify-center bg-background-primary px-6 py-10">
				<div className="w-full max-w-sm text-center">{renderContent()}</div>
			</div>
		</div>
	);
}
